import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import InputField from "components/fields/InputField";
import { authApi } from "api/endpoints";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token")?.trim() || "";

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (!token) {
      setError("Falta el token de recuperación. Usá el enlace del correo.");
      return;
    }

    setSubmitting(true);
    try {
      const { res, data } = await authApi.resetPassword({
        token,
        newPassword: password,
      });
      if (!res.ok) {
        const msg =
          data?.error ||
          data?.message ||
          (res.status === 400
            ? "El enlace expiró o no es válido. Solicitá uno nuevo."
            : "No se pudo actualizar la contraseña.");
        throw new Error(msg);
      }
      navigate("/auth/sign-in", {
        replace: true,
        state: { passwordUpdated: true },
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se pudo actualizar la contraseña.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
        <div className="mt-[10vh] w-full max-w-full md:pl-4 lg:pl-0 xl:max-w-[420px]">
          <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
            Enlace inválido
          </h4>
          <p className="mb-6 ml-1 text-base text-gray-600 dark:text-gray-300">
            Abrí el enlace que te enviamos por correo, o solicitá uno nuevo.
          </p>
          <Link
            to="/auth/forgot-password"
            className="text-sm font-semibold text-brand-500 hover:text-brand-600 dark:text-brand-300"
          >
            Solicitar recuperación de contraseña
          </Link>
          <div className="mt-4">
            <Link
              to="/auth/sign-in"
              className="text-sm font-medium text-gray-600 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white"
            >
              ← Iniciar sesión
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      <div className="mt-[10vh] w-full max-w-full md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
          Nueva contraseña
        </h4>
        <p className="mb-9 ml-1 text-base text-gray-600 dark:text-gray-300">
          Elegí una contraseña segura (mínimo 8 caracteres).
        </p>

        <form onSubmit={handleSubmit} className="w-full">
          {error ? (
            <p
              className="mb-3 ml-1 rounded-lg border border-red-400/50 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-950/40 dark:text-red-200"
              role="alert"
            >
              {error}
            </p>
          ) : null}

          <InputField
            variant="auth"
            extra="mb-3"
            label="Nueva contraseña *"
            placeholder="Mín. 8 caracteres"
            id="reset-password"
            type="password"
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
          />

          <InputField
            variant="auth"
            extra="mb-3"
            label="Confirmar contraseña *"
            placeholder="Repetí la contraseña"
            id="reset-password-confirm"
            type="password"
            value={confirm}
            onChange={(ev) => setConfirm(ev.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
          />

          <button
            type="submit"
            disabled={submitting}
            className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            {submitting ? "Guardando…" : "Guardar contraseña"}
          </button>
        </form>

        <div className="mt-6">
          <Link
            to="/auth/sign-in"
            className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-300"
          >
            ← Volver a iniciar sesión
          </Link>
        </div>
      </div>
    </div>
  );
}
